import Form, { type FormItemProps as AntFormItemProps } from "antd/lib/form";
import {
    ControllerFieldState,
    ControllerRenderProps,
    useController,
    UseFormStateReturn,
    type ControllerProps,
    type FieldPath,
    type FieldValues,
} from "react-hook-form";
import { Merge, SetOptional } from "type-fest";

const FormItem = Form.Item;

export type FormFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Merge<
    Merge<
        SetOptional<ControllerProps<TFieldValues, TName>, "render">,
        Pick<
            AntFormItemProps,
            "label" | "required" | "help" | "htmlFor" | "extra" | "tooltip" | "className" | "children"
        >
    >,
    {
        render?:
            | (({
                  field,
                  fieldState,
                  formState,
              }: {
                  field: ControllerRenderProps<TFieldValues, TName>;
                  fieldState: ControllerFieldState;
                  formState: UseFormStateReturn<TFieldValues>;
              }) => React.ReactElement)
            | undefined;
    }
>;

export function FormField<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    control,
    name,
    rules,
    shouldUnregister,
    defaultValue,
    disabled,
    render,
    children,
    ...rest
}: FormFieldProps<TFieldValues, TName>) {
    const controller = useController({
        control,
        name,
        rules,
    });

    const { error, invalid } = controller.fieldState;

    return (
        <FormItem layout="vertical" help={error?.message} validateStatus={invalid ? "error" : "success"} {...rest}>
            {render?.({ ...controller }) ?? children}
        </FormItem>
    );
}
